// Client-side duration estimates: EWMA of ms per work unit, persisted in
// localStorage so the first estimate of a session uses past runs.

const STORAGE_KEY = 'stereo.eta.v1';
const ALPHA = 0.3;

type Table = Record<string, number>;

/** Conservative defaults (ms per unit) before any measurement. */
const DEFAULTS: Table = {
  // unit = megapixel * oversample
  stereogram: 120,
  // unit = megapixel
  depth_process: 60,
  text_depth: 40,
  // unit = frame megapixel
  gif_encode: 90,
  webm_encode: 25,
  // unit = megapixel (ML inference, per frame)
  'ml:depth-anything-v2-small': 1500,
};

function read(): Table {
  try {
    return { ...DEFAULTS, ...(JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as Table) };
  } catch {
    return { ...DEFAULTS };
  }
}

let table: Table = read();

export function estimateMs(kind: string, units: number): number {
  return (table[kind] ?? 100) * Math.max(units, 0);
}

export function recordMs(kind: string, units: number, ms: number) {
  if (units <= 0 || !Number.isFinite(ms)) return;
  const per = ms / units;
  const prev = table[kind];
  table = { ...table, [kind]: prev === undefined ? per : prev * (1 - ALPHA) + per * ALPHA };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(table));
  } catch {
    /* ignore */
  }
}

/**
 * Live ETA for a batch of `total` items: once a few items are done, the
 * measured throughput replaces the prior estimate.
 */
export class BatchEta {
  private start = performance.now();
  constructor(
    private total: number,
    private priorMs: number,
  ) {}

  remainingMs(done: number): number {
    const elapsed = performance.now() - this.start;
    if (done <= 0) return Math.max(0, this.priorMs - elapsed);
    const measured = (elapsed / done) * this.total;
    // Blend: trust measurement more as progress grows.
    const w = Math.min(1, done / Math.max(2, this.total * 0.15));
    const total = w * measured + (1 - w) * Math.max(this.priorMs, elapsed);
    return Math.max(0, total - elapsed);
  }

  elapsedMs(): number {
    return performance.now() - this.start;
  }
}
